// common/hooks/useAuthentification.js
import { useEffect, useState, useCallback } from "react";
import { LOCAL_STORAGE_KEYS } from "../constants";
import { UserService } from "../services";

// Rôles ayant accès complet
const FULL_ACCESS_ROLES = ['superAdmin', 'directeur'];

// Configuration des routes avec leurs permissions (ordre de priorité)
const ROUTE_PERMISSIONS = [
    { path: '/dashboard', perm: 'dashboard:view', label: 'Tableau de bord' },
    { path: '/dashboard/clients', perm: 'clients:read', label: 'Clients' },
    { path: '/dashboard/contrats', perm: 'contrats:read', label: 'Contrats' },
    { path: '/dashboard/reglements', perm: 'reglements:read', label: 'Règlements' },
    { path: '/dashboard/impayes', perm: 'impayes:read', label: 'Impayés' },
    { path: '/dashboard/sinistres', perm: 'sinistres:read', label: 'Sinistres' },
    { path: '/dashboard/bilans', perm: 'bilans:read', label: 'Bilans & États' },
    { path: '/dashboard/compagnies', perm: 'compagnies:read', label: 'Compagnies' },
    { path: '/dashboard/types-assurance', perm: 'typesAssurance:read', label: 'Types d\'assurance' },
    { path: '/dashboard/agences', perm: 'agences:read', label: 'Agences' },
    { path: '/dashboard/parametrages', perm: 'parametrages:read', label: 'Paramétrages' },
    { path: '/dashboard/gestion-admins', perm: 'users:read', label: 'Administrateurs' },
    { path: '/dashboard/roles', perm: 'roles:read', label: 'Rôles' },
    { path: '/dashboard/audit-log', perm: 'audit:read', label: 'Journal d\'audit' },
];

// Fonction pour trouver la première page accessible
const getFirstAccessiblePage = (user) => {
    if (!user) return '/dashboard';

    // Si l'utilisateur a un rôle avec accès complet
    if (FULL_ACCESS_ROLES.includes(user.role)) {
        return '/dashboard';
    }

    const perms = new Set(user.permissions || []);

    for (const route of ROUTE_PERMISSIONS) {
        if (route.anyOf) {
            if (route.anyOf.some(p => perms.has(p))) {
                return route.path;
            }
        } else if (route.perm) {
            if (perms.has(route.perm)) {
                return route.path;
            }
        }
    }

    // Par défaut, rediriger vers le dashboard (qui montrera la page Access Denied)
    return '/dashboard';
};

export default function UseAuthentification() {
    const [currentUser, setCurrentUser] = useState(() => {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        return raw ? JSON.parse(raw) : null;
    });
    const [initialized, setInitialized] = useState(false);

    const onLogin = useCallback((loginResp, navigate) => {
        // Le serveur retourne { success: true, data: { ...user, accessToken } }
        const payload = loginResp?.data || loginResp;
        const token = payload?.accessToken || loginResp?.accessToken;
        if (token) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
        }
        // Retirer le token du user stocké
        const user = { ...payload };
        delete user.accessToken;
        localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
        setCurrentUser(user);
        setInitialized(true);

        // Rediriger vers la première page accessible
        const firstPage = getFirstAccessiblePage(user);
        navigate(firstPage);
    }, []);

    const onLogout = useCallback(async () => {
        // Tracker la deconnexion avant de supprimer le token
        try {
            const { auditService } = await import('../services');
            await auditService.trackPageVisit({ page: '/logout', title: 'Déconnexion' });
        } catch (_) {}
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        setCurrentUser(null);
        setInitialized(true);
    }, []);

    useEffect(() => {
        // réhydratation au F5
        const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        const hasUser = !!localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
        if (!token) {
            setInitialized(true);
            return;
        }
        if (hasUser) {
            setInitialized(true);
            return;
        }
        // token présent mais pas d'user en cache -> on récupère /users/me
        (async () => {
            try {
                const me = await UserService.getUserInfo();
                localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER, JSON.stringify(me));
                setCurrentUser(me);
            } catch {
                // si 401, exeRequest redirigera; on se met tout de même "ready"
            } finally {
                setInitialized(true);
            }
        })();
    }, []);

    const isAuthenticated = !!localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

    // Exposer la fonction pour utilisation externe
    const getFirstPage = useCallback(() => {
        return getFirstAccessiblePage(currentUser);
    }, [currentUser]);

    return { currentUser, setCurrentUser, onLogin, onLogout, isAuthenticated, initialized, getFirstPage };
}
